import { Box, Stack, Typography, Grid } from "@mui/material";
import { colors, shadows } from "./source.styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PercentIcon from "@mui/icons-material/Percent";
import PeopleIcon from "@mui/icons-material/People";
import CategoryIcon from "@mui/icons-material/Category";

const MetricCard = ({ icon, iconBg, title, value, subtitle, trend, trendType }) => {
  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: shadows.cardHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Row 1: Title (left) + Icon (right) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Stack>

      {/* Row 2: Bold value */}
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: colors.textPrimary,
          mb: 1,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>

      {/* Row 3: Trend or subtitle */}
      {trend && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {trendType === "up" ? (
            <TrendingUpIcon sx={{ fontSize: 14, color: colors.success }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 14, color: colors.danger }} />
          )}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: trendType === "up" ? colors.success : colors.danger,
            }}
          >
            {trend}
          </Typography>
        </Stack>
      )}

      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: colors.textSecondary,
            mt: trend ? 0 : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const MetricsCards = ({ data }) => {
  // Capitalize first letter of each word
  const capitalizeWords = (str) => {
    if (!str) return "N/A";
    return str
      .split(/[_\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const metrics = [
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 20, color: colors.chartBlue }} />,
      iconBg: "#EFF6FF",
      title: "Top Performing Source",
      value: capitalizeWords(data?.topSource),
      trend: data?.topSourceGrowth ? `${data.topSourceGrowth > 0 ? "+" : ""}${data.topSourceGrowth.toFixed(1)}% vs last mo.` : "-",
      trendType: data?.topSourceGrowth >= 0 ? "up" : "down",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 20, color: colors.chartPurple }} />,
      iconBg: "#F5F3FF",
      title: "Total Leads",
      value: data?.totalLeads?.toLocaleString() || "0",
      subtitle: "Across all sources",
    },
    {
      icon: <CategoryIcon sx={{ fontSize: 20, color: colors.chartOrange }} />,
      iconBg: "#FFFBEB",
      title: "Total Sources",
      value: data?.totalSources || "0",
      subtitle: "Active lead sources",
    },
    {
      icon: <CurrencyRupeeIcon sx={{ fontSize: 20, color: colors.success }} />,
      iconBg: "#ECFDF5",
      title: "Total Revenue",
      value: `₹${(data?.totalRevenue || 0).toLocaleString()}`,
      subtitle: "From converted leads",
    },
  ];

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <MetricCard {...metric} />
        </Grid>
      ))}
    </Grid>
  );
};

export default MetricsCards;
